import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import RightRailAdUnit from '@maxpreps/shared/corecomponents/AdUnit/RightRailAdUnit';
import LayoutManager, {
  ColumnLayout
} from '@maxpreps/shared/corecomponents/GlobalLayout/LayoutManager';
import RightRail from '@maxpreps/shared/corecomponents/GlobalLayout/RightRail';
import StyledLayoutGapManager from '@maxpreps/shared/corecomponents/GlobalLayout/StyledLayoutGapManager';
import {
  Desktop,
  Tablet
} from '@maxpreps/shared/corecomponents/ScreenSize/ScreenSize';
import { useIsDesktop } from '@maxpreps/shared/hooks/useIsScreenSize';
import { IPageConfig } from '@maxpreps/shared/models/metaTypes';
import { IChildrenProps } from '@maxpreps/shared/models/utilTypes';
import { MediaQuery } from '@maxpreps/shared/styles/breakpoint';
import CardHeader, { HeaderTag } from '@maxpreps/shared/ui/Card/CardHeader';
import StyledCard from '@maxpreps/shared/ui/Card/StyledCard';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import getContentTitle from '../../utils/getContentTitle';
import ContributionCard from './ContributionCard';

const StyledTopLayoutGapManager = styled(StyledLayoutGapManager)`
  margin-bottom: 8px;
  ${MediaQuery.Desktop} {
    margin-bottom: 16px;
  }
`;

const StyledBottomLayoutGapManager = styled(StyledLayoutGapManager)`
  margin-top: 8px;
  ${MediaQuery.Desktop} {
    margin-top: 16px;
  }
`;

interface ITeamPageLayoutProps extends IChildrenProps {
  headerActions?: ReactNode;
  pageConfig: IPageConfig;
  headerText?: string;
  headerAs?: HeaderTag;
  isDividerVisible?: boolean;
  adminToggleButton?: ReactNode;
  childrenAboveCard?: ReactNode;
  childrenBelowCard?: ReactNode;
  childrenRightRailDesktopOnly?: ReactNode;
  isContributeCardManagedByParent?: boolean;
}

const TeamPageLayout: React.FC<ITeamPageLayoutProps> = ({
  children,
  headerActions,
  pageConfig,
  headerText,
  headerAs = 'h1',
  isDividerVisible = true,
  adminToggleButton,
  childrenAboveCard,
  childrenBelowCard,
  childrenRightRailDesktopOnly = <RightRailAdUnit />,
  isContributeCardManagedByParent = false
}) => {
  const teamContext = useTeamContext();
  const { appType } = useRequestContext();
  const isDesktop = useIsDesktop();

  const h1 =
    headerText ??
    getContentTitle(pageConfig?.pageName ?? '', teamContext.data, pageConfig);

  return (
    <LayoutManager
      columnLayout={ColumnLayout.RightRailNarrow}
      pageConfig={pageConfig}
    >
      {/* Main column (div is required for css) */}
      <div>
        {/* Above the content card: always show admin toggle button on mobile */}
        {((!isDesktop && adminToggleButton) || childrenAboveCard) && (
          <StyledTopLayoutGapManager>
            <Tablet andBelow>{adminToggleButton}</Tablet>
            {childrenAboveCard}
          </StyledTopLayoutGapManager>
        )}
        {/* Main content card */}
        <StyledCard>
          <CardHeader
            title={h1 as string}
            rightAction={headerActions}
            as={headerAs}
            isDividerVisible={isDividerVisible}
          />

          {children}
        </StyledCard>
        {/* Below the content card */}
        {childrenBelowCard && (
          <StyledBottomLayoutGapManager>
            {childrenBelowCard}
          </StyledBottomLayoutGapManager>
        )}
      </div>
      {/* Right rail column (desktop only) */}
      <Desktop>
        <RightRail>
          {/* Show the admin toggle button or the contribution card (if not managed by parent) */}
          {adminToggleButton ??
            (!isContributeCardManagedByParent && !appType && (
              <ContributionCard />
            ))}
          {/* Always show right-rail ad */}
          {childrenRightRailDesktopOnly}
        </RightRail>
      </Desktop>
    </LayoutManager>
  );
};

export default TeamPageLayout;
